export interface ProjectNode {
  id: string
  parent_id?: string
  name: string
  type: "folder" | "file"
  extension?: string
  content?: string
  children?: ProjectNode[]
  [key: string]: unknown
}

/**
 * Converts the backend file-tree response (already nested with children arrays)
 * into a normalized ProjectNode tree. Normalizes file extensions so that
 * file names always include the extension (e.g. "sukesh" + "txt" → "sukesh.txt").
 */
export function buildProjectTree(items: ProjectNode[], projectName: string): ProjectNode {
  const processNode = (item: ProjectNode): ProjectNode => {
    const ext = item.extension
      ? item.extension.startsWith(".") ? item.extension.slice(1) : item.extension
      : ""
    const name =
      item.type === "file" && ext && !item.name.endsWith(`.${ext}`)
        ? `${item.name}.${ext}`
        : item.name
    return {
      ...item,
      name,
      children: item.type === "folder" ? (item.children?.map(processNode) ?? []) : undefined,
    }
  }

  return {
    id: "project-root",
    name: projectName,
    type: "folder",
    children: items.map(processNode),
  }
}

/** Recursively walk the nested tree and return every file with its full nested path. */
export function extractFilePathsFromTree(
  root: ProjectNode,
  parentPath = ""
): { path: string; content: string }[] {
  const results: { path: string; content: string }[] = []
  for (const child of root.children ?? []) {
    const childPath = parentPath ? `${parentPath}/${child.name}` : child.name
    if (child.type === "file") {
      results.push({ path: childPath, content: String(child.content ?? "") })
    } else if (child.type === "folder") {
      results.push(...extractFilePathsFromTree(child, childPath))
    }
  }
  return results
}

/** Recursively walk the nested tree and return every folder path. */
export function extractFolderPathsFromTree(
  root: ProjectNode,
  parentPath = ""
): string[] {
  const paths: string[] = []
  for (const child of root.children ?? []) {
    const childPath = parentPath ? `${parentPath}/${child.name}` : child.name
    if (child.type === "folder") {
      paths.push(childPath)
      paths.push(...extractFolderPathsFromTree(child, childPath))
    }
  }
  return paths
}

/** Recursively walk the nested tree and build a full-path → backend-id map for every node. */
export function buildIdMapFromTree(
  root: ProjectNode,
  parentPath = ""
): Map<string, string> {
  const map = new Map<string, string>()
  for (const child of root.children ?? []) {
    const childPath = parentPath ? `${parentPath}/${child.name}` : child.name
    if (child.id && child.id !== "project-root") {
      map.set(childPath, child.id)
    }
    if (child.type === "folder") {
      buildIdMapFromTree(child, childPath).forEach((id, path) => map.set(path, id))
    }
  }
  return map
}
